import { Box, BoxProps, Flex, FlexProps, useToken } from '@chakra-ui/react'
import { PricePointAttributes } from 'api-client'
import * as R from 'ramda'
import React, { useMemo } from 'react'

import { LoaderRound, StandardText } from '~shared-components/components'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { useUom } from '~shared-components/hooks/useUom'
import { pricePointAttributesToPriceTier } from '~shared-components/transforms'
import {
  getMatchingPricePoint,
  getSortedPrices,
} from '~shared-components/utils/price-utils'

import { Price } from './Price'

interface TierPriceProps {
  children?: React.ReactNode
  isLoading?: boolean
  orderAmount?: number
  pricePoints: PricePointAttributes[]
}

// strangely i do not see that '>. typography...' is doing anything here
// maybe it shoudl be without it?
// as no text is a direct child of this container
const sxPriceContainer = {
  '& > *:first-child': {
    marginBottom: 1,
  },
  '> .typography-lead': {
    marginBottom: 4,
  },
  '> .typography-text': {
    marginTop: 4,
  },
}
const PriceContainer: React.FC<BoxProps> = props => (
  <Box sx={sxPriceContainer} {...props} />
)
interface PriceTierProps extends FlexProps {
  bold?: boolean
}

const PriceTier: React.FC<PriceTierProps> = ({ bold, ...props }) => {
  const childrenColor = useToken('colors', 'blue.50')
  const sxPriceTier = useMemo(
    () => ({
      '& *': {
        color: `${childrenColor} !important`,
      },
      '& > *': {
        borderBottom: '1px solid',
        borderBottomColor: 'neutral.20',
        paddingBottom: 3,
      },
      '& > .price-tag': {
        flex: 2,
        whiteSpace: 'nowrap',
      },
      '& > .typography-text': {
        flex: 1,
        paddingRight: 2,
        whiteSpace: 'nowrap',
      },
      '&:last-child': {
        '> *': {
          borderBottom: 0,
        },
      },
      '.typography-text, .price-tag': {
        fontWeight: bold ? 'bold' : 'medium',
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bold],
  )
  return (
    <Flex className="price-tier" p="12px 24px 0" sx={sxPriceTier} {...props} />
  )
}

const PriceTierContainer: React.FC<BoxProps> = props => (
  <Box
    border="1px solid"
    borderColor="neutral.20"
    borderRadius="4px"
    marginTop={[1, 1, 'unset']}
    {...props}
  />
)

export const TierPrices = ({
  children,
  isLoading,
  orderAmount,
  pricePoints,
}: TierPriceProps) => {
  const t = useTranslations()
  const u = useUom()
  const prices = getSortedPrices(pricePoints).map(price => ({
    price,
    priceTier: pricePointAttributesToPriceTier(price),
  }))

  const matchingPricepoint = getMatchingPricePoint(pricePoints, orderAmount)
  return (
    <PriceContainer>
      {isLoading ? (
        <Flex justifyContent="center" width="100%">
          <LoaderRound />
        </Flex>
      ) : prices && prices.length > 0 ? (
        <>
          <PriceTierContainer>
            {prices.map(({ price, priceTier }, i) => {
              const isSelectedPriceTier = R.equals(matchingPricepoint, price)
              return (
                <PriceTier key={i} bold={isSelectedPriceTier}>
                  {prices.length > 1 && (
                    <StandardText>
                      {price.scaleStart.quantity}+ {u(price.scaleStart.uom)}
                    </StandardText>
                  )}
                  <Price
                    bold={isSelectedPriceTier}
                    quantity={priceTier.quantity}
                  >
                    {priceTier.price}
                  </Price>
                </PriceTier>
              )
            })}
          </PriceTierContainer>
          <StandardText color="neutral.50">
            {t('label.pricesareexclusiveofsurcharges')}
          </StandardText>
        </>
      ) : (
        children
      )}
    </PriceContainer>
  )
}
